import React, { memo, useCallback, useState } from 'react';

import { Divider, Paper } from '@mui/material';
import { CancelIcon, FilterIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import InputAdornment from 'app/shared/components/common/InputAdornment';
import { CheckboxCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useSearch from 'hooks/useSearch';

import { ActivitiesTestIds } from '../Activities.testIds';

import {
  ActivityFilterGroup,
  ActivityFilterType,
  activityFilterTypesToGroups,
} from './ActivitiesFilter.config';

const textInputProps = {
  style: { fontSize: '16px' },
};

const ActivitiesFilter = ({
  isLoading,
  availableActivityTypes,
  filteredTypes,
  setFilteredTypes,
}) => {
  const { searchText, setSearchText } = useSearch({ debounceTimeout: 500 });

  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapsed = useCallback(() => setIsCollapsed(isCollapsed => !isCollapsed), []);

  const onChangeSearchQuery = useCallback(e => setSearchText(e.target.value), [setSearchText]);

  const onClearSearchQuery = useCallback(() => setSearchText(''), [setSearchText]);

  const getParentCheckedState = useCallback(
    (name, filteredTypes) => {
      // TODO: remove filter when all types are supported
      const groupTypes = activityFilterTypesToGroups[name]?.filter(type =>
        availableActivityTypes.includes(type),
      );
      const checkedTypes = groupTypes.filter(type => filteredTypes.includes(type));

      return {
        indeterminate: !!checkedTypes.length && checkedTypes.length !== groupTypes.length,
        checked: !!checkedTypes.length && checkedTypes.length === groupTypes.length,
      };
    },
    [availableActivityTypes],
  );

  const onChangeCheckbox = useCallback(
    e => {
      const { name, checked } = e.currentTarget;
      // TODO: remove filter when all types are supported
      const children = activityFilterTypesToGroups[name]?.filter(type =>
        availableActivityTypes.includes(type),
      );
      const types = children || [name];

      setFilteredTypes(
        checked
          ? _.union(filteredTypes, types)
          : filteredTypes.filter(type => !types.includes(type)),
      );
    },
    [setFilteredTypes, filteredTypes, availableActivityTypes],
  );

  const renderOptions = useCallback(
    options =>
      options.map(name => {
        const children = activityFilterTypesToGroups[name];
        const label = children ? ActivityFilterGroup[name] : ActivityFilterType[name];
        const { indeterminate, checked } = children
          ? getParentCheckedState(name, filteredTypes)
          : { checked: filteredTypes.includes(name) };

        // TODO: remove when all types are supported
        const isEnabled = children
          ? children.some(type => availableActivityTypes.includes(type))
          : availableActivityTypes.includes(name);

        return (
          <div className="flex flex-col ml-24" key={name}>
            <CheckboxCDS
              dataTestId={ActivitiesTestIds.getFilterCheckboxId(name)}
              disabled={!isEnabled || isLoading}
              indeterminate={indeterminate}
              label={label}
              name={name}
              value={checked}
              onChange={onChangeCheckbox}
            />
            {children && renderOptions(children)}
          </div>
        );
      }),
    [availableActivityTypes, filteredTypes, getParentCheckedState, isLoading, onChangeCheckbox],
  );

  return (
    <Paper className="grow mr-5 p-8 rounded-12 text-20 border-2">
      <div className="flex items-center w-full pl-12">
        <TextFieldCDS
          fullWidth
          InputProps={textInputProps}
          ariaLabel="Search activities"
          autoComplete="off"
          dataTestId={ActivitiesTestIds.FILTER_SEARCH_INPUT}
          endAdornment={
            searchText ? (
              <InputAdornment
                component={ButtonWithTooltip}
                icon={CancelIcon}
                title="Clear"
                onClick={onClearSearchQuery}
              />
            ) : undefined
          }
          name="activitiesSearch"
          placeholder="Search..."
          type="search"
          value={searchText}
          onChange={onChangeSearchQuery}
        />
        <ButtonWithTooltip
          color={isCollapsed ? 'default' : 'secondary'}
          data-testid={ActivitiesTestIds.FILTER_TOGGLE_BUTTON}
          icon={FilterIcon}
          title="Filters"
          onClick={toggleCollapsed}
        />
      </div>

      {!isCollapsed ? (
        <>
          <Divider className="my-8" />
          <div className="flex flex-col md:flex-row">
            {renderOptions(Object.keys(ActivityFilterGroup))}
          </div>
        </>
      ) : null}
    </Paper>
  );
};

ActivitiesFilter.propTypes = {
  setFilteredTypes: PropTypes.func.isRequired,
  filteredTypes: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  availableActivityTypes: PropTypes.array.isRequired,
};

export default memo(ActivitiesFilter);
